class LocalizationCountryModal {
  constructor(modal) {
    this.modal = modal;

    this.form = modal.querySelector(
      "#LocalizationModal--country-form"
    );

    this.countryCodeInput = modal.querySelector(
      "[data-country-code-input]"
    );

    this.searchInput = modal.querySelector(
      "[data-country-search-input]"
    );

    this.countryGroups = modal.querySelector(
      "[data-country-groups]"
    );

    this.countryData = modal.querySelector(
      "[data-country-data]"
    );

    this.countryItems = [];
    this.groups = [];
    this.initialOpenGroups = [];
    this.countriesRendered = false;

    this.emptyMessage = modal.querySelector(
      "[data-country-empty]"
    );

    if (!this.form || !this.countryCodeInput) return;

    // Shopify localization 是最终状态；URL 仅用于反映当前国家，
    // 不再反向触发国家切换。
    this.updateCountryParam(this.countryCodeInput.value);

    this.bindCountryItems();
    this.bindSearch();
    this.watchModalOpen();
  }

  renderCountries() {
    if (this.countriesRendered || !this.countryGroups || !this.countryData) {
      return;
    }

    let data;

    try {
      data = JSON.parse(this.countryData.textContent);
    } catch (error) {
      this.countryGroups.removeAttribute("aria-busy");
      return;
    }

    const currentCountryCode = this.countryGroups.dataset.currentCountryCode;
    const currentGroup = this.countryGroups.dataset.currentCountryGroup;
    const fragment = document.createDocumentFragment();

    data.groups.forEach((groupData) => {
      const countries = data.countries.filter(
        (country) => country.g === groupData.i
      );

      // Keep these destinations together at China's alphabetical position.
      const chinaCodes = ["CN", "HK", "MO", "TW"];
      const chinaName = countries.find((country) => country.c === "CN")?.n || "China-Mainland";
      countries.sort((a, b) => {
        const aRank = chinaCodes.indexOf(a.c);
        const bRank = chinaCodes.indexOf(b.c);
        if (aRank !== -1 && bRank !== -1) return aRank - bRank;
        const aName = aRank !== -1 ? chinaName : a.n;
        const bName = bRank !== -1 ? chinaName : b.n;
        return aName.localeCompare(bName, document.documentElement.lang || "en");
      });

      if (countries.length === 0) return;

      const group = document.createElement("details");
      group.className = "localization-country-group";
      group.dataset.countryGroup = "";
      group.dataset.countryGroupId = groupData.i;
      group.open = groupData.i === currentGroup;

      const summary = document.createElement("summary");
      summary.className = "localization-country-group__summary";

      const title = document.createElement("span");
      title.className = "localization-country-group__title";
      title.textContent = groupData.t;

      const arrow = document.createElement("span");
      arrow.className = "localization-country-group__arrow";
      arrow.setAttribute("aria-hidden", "true");
      arrow.innerHTML = '<svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

      summary.append(title, arrow);

      const list = document.createElement("div");
      list.className = "localization-country-group__list";

      countries.forEach((country) => {
        list.appendChild(
          this.createCountryItem(country, currentCountryCode)
        );
      });

      group.append(summary, list);
      fragment.appendChild(group);
    });

    this.countryGroups.replaceChildren(fragment);
    this.countryGroups.removeAttribute("aria-busy");
    this.countryData.remove();
    this.countryItems = Array.from(
      this.countryGroups.querySelectorAll("[data-country-item]")
    );
    this.groups = Array.from(
      this.countryGroups.querySelectorAll("[data-country-group]")
    );
    this.initialOpenGroups = this.groups
      .filter((group) => group.open)
      .map((group) => group.dataset.countryGroupId);
    this.countriesRendered = true;
  }

  createCountryItem(country, currentCountryCode) {
    const isCurrentCountry = country.c === currentCountryCode;
    const item = document.createElement("button");
    item.type = "button";
    item.className = `localization-country-item${isCurrentCountry ? " is-current" : ""}`;
    item.dataset.countryItem = "";
    item.dataset.countryCode = country.c;
    item.dataset.countrySearch = `${country.n} ${country.c} ${country.u}`;

    if (isCurrentCountry) {
      item.setAttribute("aria-current", "true");
    }

    const main = document.createElement("span");
    main.className = "localization-country-item__main";

    const flag = document.createElement("span");
    flag.className = "localization-country-item__flag";

    const image = document.createElement("img");
    image.src = country.f;
    image.alt = country.n;
    image.width = 24;
    image.height = 24;
    image.loading = "lazy";
    image.decoding = "async";
    flag.appendChild(image);

    const name = document.createElement("span");
    name.className = "localization-country-item__name";
    name.textContent = country.n;
    main.append(flag, name);

    const right = document.createElement("span");
    right.className = "localization-country-item__right";

    const currency = document.createElement("span");
    currency.className = "localization-country-item__currency";
    currency.textContent = country.u;
    right.appendChild(currency);

    if (isCurrentCountry) {
      const check = document.createElement("span");
      check.className = "localization-country-item__check";
      check.setAttribute("aria-hidden", "true");
      check.innerHTML = '<svg width="15" height="12" viewBox="0 0 15 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 6L5.2 10L14 1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      right.appendChild(check);
    }

    item.append(main, right);
    return item;
  }

  updateCountryParam(countryCode) {
    const url = new URL(window.location.href);

    url.searchParams.set("region", countryCode);

    history.replaceState({}, "", url.toString());
  }

  bindCountryItems() {
    if (!this.countryGroups) return;

    this.countryGroups.addEventListener("click", (event) => {
      const item = event.target.closest("[data-country-item]");

      if (!item) return;

      const countryCode = item.dataset.countryCode;

      if (!countryCode) return;

      // 当前国家不重复提交
      if (item.getAttribute("aria-current") === "true") {
        return;
      }

      // 用户提交的 country_code 是唯一切换来源；URL 只记录该选择。
      this.updateCountryParam(countryCode);

      this.countryCodeInput.value = countryCode;

      this.countryItems.forEach((countryItem) => {
        countryItem.disabled = true;
        countryItem.classList.add("is-loading");
      });

      if (typeof this.form.requestSubmit === "function") {
        this.form.requestSubmit();
      } else {
        this.form.submit();
      }
    });
  }

  bindSearch() {
    if (!this.searchInput) return;

    this.searchInput.addEventListener("input", () => {
      this.renderCountries();
      this.filterCountries(this.searchInput.value);
    });

    this.searchInput.addEventListener("search", () => {
      this.renderCountries();
      this.filterCountries(this.searchInput.value);
    });
  }

  watchModalOpen() {
    const observer = new MutationObserver(() => {
      if (!this.modal.hasAttribute("open")) return;

      this.renderCountries();

      if (this.searchInput) {
        this.searchInput.value = "";
      }

      this.restoreDefaultGroups();
      this.filterCountries("");
    });

    observer.observe(this.modal, {
      attributes: true,
      attributeFilter: ["open"],
    });
  }

  normalizeText(value) {
    return String(value || "")
      .toLocaleLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim();
  }

  restoreDefaultGroups() {
    this.groups.forEach((group) => {
      const groupId = group.dataset.countryGroupId;

      group.open = this.initialOpenGroups.includes(groupId);
      group.hidden = false;
    });
  }

  filterCountries(value) {
    const query = this.normalizeText(value);
    let totalVisibleCountries = 0;

    this.groups.forEach((group) => {
      const items = Array.from(
        group.querySelectorAll("[data-country-item]")
      );

      let visibleCountriesInGroup = 0;

      items.forEach((item) => {
        const searchText = this.normalizeText(
          item.dataset.countrySearch
        );

        const isVisible =
          query === "" || searchText.includes(query);

        item.hidden = !isVisible;

        if (isVisible) {
          visibleCountriesInGroup += 1;
          totalVisibleCountries += 1;
        }
      });

      group.hidden = visibleCountriesInGroup === 0;

      if (query !== "" && visibleCountriesInGroup > 0) {
        group.open = true;
      }

      if (query === "") {
        const groupId = group.dataset.countryGroupId;

        group.open = this.initialOpenGroups.includes(groupId);
      }
    });

    if (this.emptyMessage) {
      this.emptyMessage.hidden = totalVisibleCountries > 0;
    }
  }
}

function initializeLocalizationCountryModal() {
  const modal = document.querySelector(
    "#LocalizationModal--country"
  );

  if (!modal) return;

  if (modal.dataset.countryModalInitialized === "true") {
    return;
  }

  modal.dataset.countryModalInitialized = "true";

  new LocalizationCountryModal(modal);
}

if (document.readyState === "loading") {
  document.addEventListener(
    "DOMContentLoaded",
    initializeLocalizationCountryModal
  );
} else {
  initializeLocalizationCountryModal();
}

document.addEventListener(
  "shopify:section:load",
  initializeLocalizationCountryModal
);
